/**
 * Layered flowchart layout.
 *
 * A Sugiyama-style pipeline, specialised for flowcharts rather than general
 * graphs:
 *
 *   1. split into weakly connected components (the main flow and each
 *      standalone function are laid out independently, then placed side by side)
 *   2. rank nodes into layers by longest path, ignoring back edges
 *   3. order nodes within each layer, seeded by the compiler's emission order
 *      and refined by median sweeps to cut crossings
 *   4. assign coordinates: layer offsets along the flow axis, median-driven
 *      packing across it
 *   5. compute group frames and overall bounds
 *
 * Two properties are load-bearing and everything here is written to preserve
 * them:
 *
 * - **Determinism.** No `Math.random`, no `Date`, no iteration over unordered
 *   containers without an explicit sort. The same graph always produces the
 *   same coordinates, on any machine — which is what lets the layout be tested
 *   and lets an exported SVG match the screen.
 *
 * - **Stability.** Seeding the order from the compiler's emission order (which
 *   follows source order) means an edit that does not change the structure does
 *   not reshuffle the diagram.
 */
import type {
  FlowDirection,
  FlowGraph,
  GraphEdge,
  GraphGroup,
  GraphNode,
  Point,
  Rect,
} from '../model/types';
import { computeBounds } from '../model/types';

export interface LayoutOptions {
  readonly direction: FlowDirection;
  /** Gap between neighbouring nodes inside one layer. */
  readonly nodeSpacing: number;
  /** Gap between consecutive layers, along the flow axis. */
  readonly rankSpacing: number;
  /** Gap between disconnected components. */
  readonly componentSpacing: number;
  /** Padding between a function frame and the nodes it encloses. */
  readonly groupPadding: number;
  /** Median-sweep iterations. More is tidier and slower; 4 is the sweet spot. */
  readonly crossingSweeps: number;
  /** Extra room reserved around a loop condition for its back edge. */
  readonly loopMargin: number;
}

export const DEFAULT_LAYOUT_OPTIONS: LayoutOptions = {
  direction: 'top-to-bottom',
  nodeSpacing: 48,
  rankSpacing: 64,
  componentSpacing: 120,
  groupPadding: 20,
  crossingSweeps: 4,
  loopMargin: 44,
};

/** Positions every node, sizes every group, and fills in `bounds`. */
export function layoutGraph(graph: FlowGraph, options: Partial<LayoutOptions> = {}): FlowGraph {
  const opts: LayoutOptions = {
    ...DEFAULT_LAYOUT_OPTIONS,
    ...options,
    direction: options.direction ?? graph.direction,
  };

  if (graph.nodes.length === 0) {
    return { ...graph, direction: opts.direction, bounds: null };
  }

  const engine = new LayoutEngine(graph, opts);
  const positions = engine.run();

  const nodes = graph.nodes.map((node) => ({
    ...node,
    position: positions.get(node.id) ?? { x: 0, y: 0 },
  }));

  const groups = sizeGroups(graph.groups, nodes, opts.groupPadding);

  return {
    ...graph,
    nodes,
    groups,
    direction: opts.direction,
    bounds: computeBounds(nodes),
  };
}

/* ------------------------------------------------------------------ *
 * Engine
 * ------------------------------------------------------------------ */

interface Adjacency {
  readonly successors: ReadonlyMap<string, readonly string[]>;
  readonly predecessors: ReadonlyMap<string, readonly string[]>;
}

class LayoutEngine {
  private readonly nodesById = new Map<string, GraphNode>();
  /** Edges that participate in ranking — back edges are excluded. */
  private readonly forwardEdges: GraphEdge[] = [];
  private readonly order: ReadonlyMap<string, number>;

  constructor(
    private readonly graph: FlowGraph,
    private readonly options: LayoutOptions,
  ) {
    for (const node of graph.nodes) this.nodesById.set(node.id, node);
    this.order = new Map(graph.nodes.map((node, index) => [node.id, index]));
    this.forwardEdges = this.selectForwardEdges();
  }

  /**
   * Everything the ranking pass may follow.
   *
   * The compiler already flags loop back edges, but a graph can still contain a
   * cycle it did not label — an `@id` collision that merges two nodes, say — so
   * a DFS pass removes whatever cycles remain. Ranking must run on a DAG.
   */
  private selectForwardEdges(): GraphEdge[] {
    const candidates = this.graph.edges.filter(
      (edge) =>
        !edge.isBackEdge &&
        edge.source !== edge.target &&
        this.nodesById.has(edge.source) &&
        this.nodesById.has(edge.target),
    );

    const successors = new Map<string, GraphEdge[]>();
    for (const edge of candidates) {
      const list = successors.get(edge.source);
      if (list) list.push(edge);
      else successors.set(edge.source, [edge]);
    }

    const WHITE = 0;
    const GREY = 1;
    const BLACK = 2;
    const colour = new Map<string, number>();
    const removed = new Set<string>();

    // Iterative DFS: recursion would blow the stack on a 4000-node benchmark.
    for (const root of this.sortedNodeIds()) {
      if ((colour.get(root) ?? WHITE) !== WHITE) continue;
      const stack: Array<{ id: string; index: number }> = [{ id: root, index: 0 }];
      colour.set(root, GREY);

      while (stack.length > 0) {
        const frame = stack[stack.length - 1]!;
        const outgoing = successors.get(frame.id) ?? [];
        if (frame.index >= outgoing.length) {
          colour.set(frame.id, BLACK);
          stack.pop();
          continue;
        }
        const edge = outgoing[frame.index]!;
        frame.index += 1;
        if (removed.has(edge.id)) continue;

        const state = colour.get(edge.target) ?? WHITE;
        if (state === GREY) {
          removed.add(edge.id); // closes a cycle
        } else if (state === WHITE) {
          colour.set(edge.target, GREY);
          stack.push({ id: edge.target, index: 0 });
        }
      }
    }

    return candidates.filter((edge) => !removed.has(edge.id));
  }

  private sortedNodeIds(): string[] {
    return [...this.nodesById.keys()].sort(
      (a, b) => (this.order.get(a) ?? 0) - (this.order.get(b) ?? 0),
    );
  }

  private buildAdjacency(edges: readonly GraphEdge[]): Adjacency {
    const successors = new Map<string, string[]>();
    const predecessors = new Map<string, string[]>();
    for (const edge of edges) {
      (successors.get(edge.source) ?? successors.set(edge.source, []).get(edge.source)!).push(
        edge.target,
      );
      (predecessors.get(edge.target) ?? predecessors.set(edge.target, []).get(edge.target)!).push(
        edge.source,
      );
    }
    return { successors, predecessors };
  }

  run(): Map<string, Point> {
    const components = this.findComponents();
    const positions = new Map<string, Point>();

    // Components are laid out independently, then packed across the axis
    // perpendicular to the flow so the main flow and each standalone function
    // sit next to one another rather than on top of each other.
    let offset = 0;
    for (const component of components) {
      const local = this.layoutComponent(component);
      const bounds = boundsOf(local, this.nodesById);
      if (!bounds) continue;

      for (const [id, point] of local) {
        positions.set(
          id,
          this.options.direction === 'top-to-bottom'
            ? { x: point.x - bounds.minX + offset, y: point.y - bounds.minY }
            : { x: point.x - bounds.minX, y: point.y - bounds.minY + offset },
        );
      }

      offset +=
        (this.options.direction === 'top-to-bottom' ? bounds.width : bounds.height) +
        this.options.componentSpacing;
    }

    return positions;
  }

  /** Weakly connected components, in first-appearance order. */
  private findComponents(): string[][] {
    const neighbours = new Map<string, string[]>();
    const add = (a: string, b: string): void => {
      (neighbours.get(a) ?? neighbours.set(a, []).get(a)!).push(b);
    };
    for (const edge of this.graph.edges) {
      if (!this.nodesById.has(edge.source) || !this.nodesById.has(edge.target)) continue;
      add(edge.source, edge.target);
      add(edge.target, edge.source);
    }

    const seen = new Set<string>();
    const components: string[][] = [];

    for (const id of this.sortedNodeIds()) {
      if (seen.has(id)) continue;
      const component: string[] = [];
      const queue = [id];
      seen.add(id);
      while (queue.length > 0) {
        const current = queue.shift()!;
        component.push(current);
        for (const next of neighbours.get(current) ?? []) {
          if (seen.has(next)) continue;
          seen.add(next);
          queue.push(next);
        }
      }
      component.sort((a, b) => (this.order.get(a) ?? 0) - (this.order.get(b) ?? 0));
      components.push(component);
    }

    return components;
  }

  private layoutComponent(component: readonly string[]): Map<string, Point> {
    const member = new Set(component);
    const edges = this.forwardEdges.filter((e) => member.has(e.source) && member.has(e.target));
    const adjacency = this.buildAdjacency(edges);

    const ranks = this.assignRanks(component, adjacency);
    const layers = this.buildLayers(component, ranks);
    this.reduceCrossings(layers, adjacency);
    return this.assignCoordinates(layers);
  }

  /* --- 2. ranking ---------------------------------------------------- */

  /** Longest-path ranking over the DAG, so every edge points strictly downward. */
  private assignRanks(component: readonly string[], adjacency: Adjacency): Map<string, number> {
    const indegree = new Map<string, number>();
    for (const id of component) indegree.set(id, 0);
    for (const id of component) {
      for (const next of adjacency.successors.get(id) ?? []) {
        indegree.set(next, (indegree.get(next) ?? 0) + 1);
      }
    }

    const ranks = new Map<string, number>();
    const queue = component.filter((id) => (indegree.get(id) ?? 0) === 0);
    for (const id of queue) ranks.set(id, 0);

    let head = 0;
    while (head < queue.length) {
      const id = queue[head]!;
      head += 1;
      const rank = ranks.get(id) ?? 0;
      for (const next of adjacency.successors.get(id) ?? []) {
        ranks.set(next, Math.max(ranks.get(next) ?? 0, rank + 1));
        const remaining = (indegree.get(next) ?? 0) - 1;
        indegree.set(next, remaining);
        if (remaining === 0) queue.push(next);
      }
    }

    // Anything the topological walk could not reach (only possible if a cycle
    // survived) still needs a rank; put it after its earliest predecessor.
    for (const id of component) {
      if (ranks.has(id)) continue;
      const preds = adjacency.predecessors.get(id) ?? [];
      const best = preds.reduce((max, p) => Math.max(max, ranks.get(p) ?? 0), -1);
      ranks.set(id, best + 1);
    }

    return ranks;
  }

  private buildLayers(component: readonly string[], ranks: Map<string, number>): string[][] {
    const maxRank = component.reduce((max, id) => Math.max(max, ranks.get(id) ?? 0), 0);
    const layers: string[][] = Array.from({ length: maxRank + 1 }, () => []);
    for (const id of component) {
      layers[ranks.get(id) ?? 0]!.push(id);
    }
    for (const layer of layers) {
      layer.sort((a, b) => (this.order.get(a) ?? 0) - (this.order.get(b) ?? 0));
    }
    return layers;
  }

  /* --- 3. crossing reduction ----------------------------------------- */

  /**
   * Alternating median sweeps. A node moves to the median position of its
   * neighbours in the adjacent layer; ties keep their previous order, which is
   * what makes the result deterministic and edit-stable.
   */
  private reduceCrossings(layers: string[][], adjacency: Adjacency): void {
    if (layers.length < 2) return;

    const indexIn = (layer: readonly string[]): Map<string, number> =>
      new Map(layer.map((id, index) => [id, index]));

    for (let sweep = 0; sweep < this.options.crossingSweeps; sweep += 1) {
      const downward = sweep % 2 === 0;

      if (downward) {
        for (let i = 1; i < layers.length; i += 1) {
          this.sortByMedian(layers[i]!, indexIn(layers[i - 1]!), adjacency.predecessors);
        }
      } else {
        for (let i = layers.length - 2; i >= 0; i -= 1) {
          this.sortByMedian(layers[i]!, indexIn(layers[i + 1]!), adjacency.successors);
        }
      }
    }
  }

  private sortByMedian(
    layer: string[],
    reference: ReadonlyMap<string, number>,
    neighbours: ReadonlyMap<string, readonly string[]>,
  ): void {
    const previous = new Map(layer.map((id, index) => [id, index]));
    const median = new Map<string, number>();

    for (const id of layer) {
      const positions = (neighbours.get(id) ?? [])
        .map((n) => reference.get(n))
        .filter((v): v is number => v !== undefined)
        .sort((a, b) => a - b);

      if (positions.length === 0) {
        // No anchor: keep it where it was rather than collapsing it to zero.
        median.set(id, previous.get(id) ?? 0);
        continue;
      }
      const mid = positions.length >> 1;
      median.set(
        id,
        positions.length % 2 === 1 ? positions[mid]! : (positions[mid - 1]! + positions[mid]!) / 2,
      );
    }

    layer.sort((a, b) => {
      const delta = (median.get(a) ?? 0) - (median.get(b) ?? 0);
      if (delta !== 0) return delta;
      return (previous.get(a) ?? 0) - (previous.get(b) ?? 0);
    });
  }

  /* --- 4. coordinates ------------------------------------------------- */

  private assignCoordinates(layers: readonly string[][]): Map<string, Point> {
    const vertical = this.options.direction === 'top-to-bottom';
    const { nodeSpacing, rankSpacing, loopMargin } = this.options;

    /** Position along the flow axis for each layer. */
    const layerOffsets: number[] = [];
    let cursor = 0;
    for (const layer of layers) {
      layerOffsets.push(cursor);
      const thickness = layer.reduce((max, id) => {
        const node = this.nodesById.get(id);
        if (!node) return max;
        return Math.max(max, vertical ? node.size.height : node.size.width);
      }, 0);
      // Loop conditions need room for the back edge to come round.
      const hasLoop = layer.some((id) => this.nodesById.get(id)?.role === 'loop');
      cursor += thickness + rankSpacing + (hasLoop ? loopMargin : 0);
    }

    /** Position across the flow axis — the part worth iterating on. */
    const cross = new Map<string, number>();

    // Seed: pack each layer edge to edge, centred on zero.
    for (const layer of layers) {
      let x = 0;
      for (const id of layer) {
        const node = this.nodesById.get(id);
        if (!node) continue;
        cross.set(id, x);
        x += (vertical ? node.size.width : node.size.height) + nodeSpacing;
      }
      this.centreLayer(layer, cross, vertical);
    }

    const adjacency = this.buildAdjacency(
      this.forwardEdges.filter((e) => cross.has(e.source) && cross.has(e.target)),
    );

    // Pull each node toward the median of its neighbours, then push overlaps
    // apart. Alternating direction keeps long chains straight.
    for (let sweep = 0; sweep < this.options.crossingSweeps + 2; sweep += 1) {
      const downward = sweep % 2 === 0;
      const indices = downward
        ? layers.map((_, i) => i)
        : layers.map((_, i) => layers.length - 1 - i);

      for (const index of indices) {
        const layer = layers[index]!;
        const neighbourMap = downward ? adjacency.predecessors : adjacency.successors;
        const desired = new Map<string, number>();

        for (const id of layer) {
          const node = this.nodesById.get(id);
          if (!node) continue;
          const half = (vertical ? node.size.width : node.size.height) / 2;
          const anchors = (neighbourMap.get(id) ?? [])
            .map((n) => {
              const other = this.nodesById.get(n);
              const base = cross.get(n);
              if (!other || base === undefined) return undefined;
              return base + (vertical ? other.size.width : other.size.height) / 2;
            })
            .filter((v): v is number => v !== undefined)
            .sort((a, b) => a - b);

          if (anchors.length === 0) {
            desired.set(id, cross.get(id) ?? 0);
            continue;
          }
          const mid = anchors.length >> 1;
          const centre =
            anchors.length % 2 === 1 ? anchors[mid]! : (anchors[mid - 1]! + anchors[mid]!) / 2;
          desired.set(id, centre - half);
        }

        this.packLayer(layer, desired, cross, vertical);
      }
    }

    for (const layer of layers) this.centreLayer(layer, cross, vertical);

    const positions = new Map<string, Point>();
    layers.forEach((layer, index) => {
      const along = layerOffsets[index]!;
      const thickness = layer.reduce((max, id) => {
        const node = this.nodesById.get(id);
        if (!node) return max;
        return Math.max(max, vertical ? node.size.height : node.size.width);
      }, 0);

      for (const id of layer) {
        const node = this.nodesById.get(id);
        if (!node) continue;
        // Centre each node inside its layer band so mixed-height rows line up.
        const own = vertical ? node.size.height : node.size.width;
        const centred = along + (thickness - own) / 2;
        const across = cross.get(id) ?? 0;
        positions.set(
          id,
          vertical
            ? { x: round(across), y: round(centred) }
            : { x: round(centred), y: round(across) },
        );
      }
    });

    return positions;
  }

  /**
   * Places nodes as close to `desired` as possible while keeping their layer
   * order and a minimum gap. Two passes — left to right, then right to left —
   * which is enough to settle without oscillating.
   */
  private packLayer(
    layer: readonly string[],
    desired: ReadonlyMap<string, number>,
    cross: Map<string, number>,
    vertical: boolean,
  ): void {
    const spacing = this.options.nodeSpacing;
    const extent = (id: string): number => {
      const node = this.nodesById.get(id);
      if (!node) return 0;
      return vertical ? node.size.width : node.size.height;
    };

    let limit = -Infinity;
    for (const id of layer) {
      const want = desired.get(id) ?? cross.get(id) ?? 0;
      const placed = Math.max(want, limit);
      cross.set(id, placed);
      limit = placed + extent(id) + spacing;
    }

    limit = Infinity;
    for (let i = layer.length - 1; i >= 0; i -= 1) {
      const id = layer[i]!;
      const want = desired.get(id) ?? cross.get(id) ?? 0;
      const current = cross.get(id) ?? want;
      // Only pull a node back toward its target; never push it past a neighbour.
      const placed = Math.min(Math.max(want, current), limit - extent(id));
      cross.set(id, placed);
      limit = placed - spacing;
    }

    // The backward pass can violate the forward gap; one more forward pass
    // restores it without undoing the improvement.
    limit = -Infinity;
    for (const id of layer) {
      const placed = Math.max(cross.get(id) ?? 0, limit);
      cross.set(id, placed);
      limit = placed + extent(id) + spacing;
    }
  }

  private centreLayer(
    layer: readonly string[],
    cross: Map<string, number>,
    vertical: boolean,
  ): void {
    if (layer.length === 0) return;
    let min = Infinity;
    let max = -Infinity;
    for (const id of layer) {
      const node = this.nodesById.get(id);
      const start = cross.get(id);
      if (!node || start === undefined) continue;
      min = Math.min(min, start);
      max = Math.max(max, start + (vertical ? node.size.width : node.size.height));
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return;
    const shift = -(min + max) / 2;
    for (const id of layer) {
      cross.set(id, (cross.get(id) ?? 0) + shift);
    }
  }
}

/* ------------------------------------------------------------------ *
 * Helpers
 * ------------------------------------------------------------------ */

/** Half-pixel coordinates make SVG strokes blurry; snap everything to integers. */
function round(value: number): number {
  return Math.round(value);
}

function boundsOf(
  positions: ReadonlyMap<string, Point>,
  nodesById: ReadonlyMap<string, GraphNode>,
): { minX: number; minY: number; width: number; height: number } | null {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const [id, point] of positions) {
    const node = nodesById.get(id);
    if (!node) continue;
    minX = Math.min(minX, point.x);
    minY = Math.min(minY, point.y);
    maxX = Math.max(maxX, point.x + node.size.width);
    maxY = Math.max(maxY, point.y + node.size.height);
  }
  if (!Number.isFinite(minX)) return null;
  return { minX, minY, width: maxX - minX, height: maxY - minY };
}

/** Bounding box of each group's member nodes, expanded by `padding`. */
function sizeGroups(
  groups: readonly GraphGroup[],
  nodes: readonly GraphNode[],
  padding: number,
): GraphGroup[] {
  if (groups.length === 0) return [];
  const byId = new Map(nodes.map((node) => [node.id, node]));

  return groups.map((group) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    for (const id of group.nodeIds) {
      const node = byId.get(id);
      if (!node) continue;
      minX = Math.min(minX, node.position.x);
      minY = Math.min(minY, node.position.y);
      maxX = Math.max(maxX, node.position.x + node.size.width);
      maxY = Math.max(maxY, node.position.y + node.size.height);
    }

    if (!Number.isFinite(minX)) return { ...group, bounds: null };

    // Extra headroom at the top for the frame's caption.
    const bounds: Rect = {
      x: minX - padding,
      y: minY - padding - 12,
      width: maxX - minX + padding * 2,
      height: maxY - minY + padding * 2 + 12,
    };
    return { ...group, bounds };
  });
}
